<template>
  <div class="image-upload-container">
    <!-- el-upload 仅用于触发文件选择，不自动上传 -->
    <el-upload
      ref="uploader"
      class="hidden-uploader"
      :show-file-list="false"
      :on-change="handleFileSelect"  
      :before-upload="beforeUploadCheck" 
      :auto-upload="false"
      accept="image/*"
      @click.stop
    >
    </el-upload>

    <!-- 自定义上传区域 -->
    <div
      class="custom-upload-area"
      :class="{ 'has-image': internalFileList.length }"
      @click.stop="triggerFileInput"
    >
      <!-- 无图片时的占位符 -->
      <template v-if="!internalFileList.length">
        <el-icon class="upload-icon"><Plus /></el-icon>
        <span class="upload-text">点击上传</span>
      </template>

      <!-- 有图片时的预览和操作 -->
      <template v-else>
        <div
          class="preview-wrapper"
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
          @click.stop
        >
          <img :src="internalFileList[0].url" class="preview-image" alt="预览">
          <!-- 悬浮操作层 -->
          <transition name="fade">
            <div v-show="isHovering" class="action-overlay">
              <el-tooltip effect="dark" content="预览" placement="top">
                <el-icon class="action-icon" @click.stop="handlePreview">
                  <ZoomIn />
                </el-icon>
              </el-tooltip>
              <el-tooltip effect="dark" content="删除" placement="top">
                <el-icon class="action-icon" @click.stop="handleRemove">
                  <Delete />
                </el-icon>
              </el-tooltip>
            </div>
          </transition>
        </div>
      </template>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="图片预览"
      append-to-body
      :modal="true"
      @click.stop
    >
      <img style="display: block; width: 100%; height: auto;" :src="dialogImageUrl" alt="图片预览" @click.stop />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { ElUpload, ElIcon, ElTooltip, ElDialog, ElMessage } from 'element-plus';
import { Plus, Delete, ZoomIn } from '@element-plus/icons-vue';
// 导入新的服务和 API 函数
import { getQiniuToken } from '@/api/qiniu'; // !! 确保路径正确 !!
import { uploadToQiniu } from '@/services/uploader'; // !! 确保路径正确 !!

const props = defineProps({
  modelValue: String, // 图片的 key 或 URL (用于 v-model)
  fileList: {         // 外部文件列表 (用于 v-model:fileList)
    type: Array,
    default: () => []
  },
  maxSize: {          // 最大文件大小 (MB)
    type: Number,
    default: 2
  }
});

const emit = defineEmits([
  'update:modelValue',
  'update:fileList',
  'upload-success',
  'upload-error',
  'file-dialog-opened',
  'file-dialog-closed'
]);

// --- 组件内部状态 ---
const uploader = ref(null); // el-upload 组件引用
const internalFileList = ref([]); // 组件内部维护的文件列表，用于展示
const dialogVisible = ref(false); // 预览对话框可见性
const dialogImageUrl = ref(''); // 预览图片 URL
const isHovering = ref(false); // 是否悬浮在预览图上
let localPreviewUrl = null; // 本地预览 URL，需要手动管理释放
const isUploading = ref(false); // 是否正在上传的状态

// --- 核心逻辑 ---

/**
 * 从 props 更新内部文件列表状态
 * !! 关键优化：此函数不再 emit update:fileList !!
 */
const updateInternalListFromProps = () => {
  let newInternalList = [];
  const currentInternalJSON = JSON.stringify(internalFileList.value);

  if (props.modelValue) {
    // 如果有 modelValue (通常是已上传图片的 key 或 URL)
    const url = props.modelValue.startsWith('http') ? props.modelValue : `http://image.langtaichem.com/${props.modelValue}`; // 替换为你的图片域名
    newInternalList = [{
      name: props.modelValue, // 使用 key 或 URL 作为 name
      url: url,
      status: 'success', // 假定已上传成功
      uid: Date.now() + Math.random() // 给一个唯一的 uid
    }];
  } else if (props.fileList?.length && props.fileList[0]?.url) {
    // 如果没有 modelValue，但有 fileList prop (可能是外部传入的初始列表)
    // 确保创建新数组引用，避免直接修改 prop
    newInternalList = JSON.parse(JSON.stringify(props.fileList));
  }
  // else { // 都没有，则为空列表 }

  // 只有当内容真正变化时才更新内部状态，防止无限循环
  if (JSON.stringify(newInternalList) !== currentInternalJSON) {
    // console.log('ImageUpload: Updating internal file list from props.');
    internalFileList.value = newInternalList;
  }
};

/** 触发文件选择框 */
const triggerFileInput = () => {
  // 只有在没有图片且未在上传时才允许触发
  if (!internalFileList.value.length && uploader.value && !isUploading.value) {
    emit('file-dialog-opened');
    const inputElement = uploader.value.$el?.querySelector('input[type="file"]');
    if (inputElement) {
      inputElement.value = null; // 清空上次选择，确保 on-change 总是触发
      inputElement.click();
    } else {
      console.warn('ImageUpload: 无法找到文件输入元素');
      ElMessage.warning('无法打开文件选择器');
      emit('file-dialog-closed'); // 即使失败也要关闭状态
    }
  }
};

/**
 * el-upload 的 before-upload 钩子 (仅用于校验)
 * @param {File} rawFile 原始文件对象
 */
const beforeUploadCheck = (rawFile) => {
  const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const isImage = validImageTypes.includes(rawFile.type);
  const isLtMaxSize = rawFile.size / 1024 / 1024 < props.maxSize;

  if (!isImage) {
    ElMessage.error('只能上传图片文件（JPEG/PNG/GIF/WEBP）');
    emit('file-dialog-closed'); // 校验失败也关闭状态
    return false; // 阻止 el-upload 继续处理
  }
  if (!isLtMaxSize) {
    ElMessage.error(`图片大小不能超过 ${props.maxSize}MB`);
    emit('file-dialog-closed'); // 校验失败也关闭状态
    return false; // 阻止 el-upload 继续处理
  }
  // 这里返回 false 是因为我们不使用 el-upload 的自动或手动上传，只用它选文件
  // return false;
  // 更新：发现 on-change 需要 before-upload 返回 true 或 Promise.resolve() 才能拿到 File 对象
  // 但我们依然不希望 el-upload 管理上传列表和状态，所以校验后直接返回 true 或 false
  return true; // 或者根据校验结果返回 false
};

/**
 * el-upload 的 on-change 钩子，文件选择后触发
 * @param {object} uploadFile el-upload 包装的文件对象
 */
const handleFileSelect = async (uploadFile) => {
  // console.log('ImageUpload: handleFileSelect triggered', uploadFile);

  // 检查文件状态，通常刚选择的是 'ready'
  if (uploadFile.status !== 'ready') {
    // 忽略非 ready 状态的文件，例如 el-upload 内部移除文件时也可能触发 on-change
    return;
  }

  // 检查文件是否通过了 beforeUploadCheck
  if (!beforeUploadCheck(uploadFile.raw)) {
      // 如果校验不通过，需要手动清理 el-upload 内部的文件列表（如果需要）
      // uploader.value?.clearFiles(); // 可选，看是否影响下次选择
      emit('file-dialog-closed');
      return;
  }

  // --- 开始处理上传 ---
  isUploading.value = true; // 设置上传中状态

  // 释放之前的本地预览 URL
  if (localPreviewUrl) {
    URL.revokeObjectURL(localPreviewUrl);
    localPreviewUrl = null;
  }
  // 创建新的本地预览 URL
  localPreviewUrl = URL.createObjectURL(uploadFile.raw);

  // 更新内部文件列表为上传中状态
  const uploadingFileEntry = {
    name: uploadFile.name,
    url: localPreviewUrl, // 使用本地 URL 进行预览
    status: 'uploading',
    uid: uploadFile.uid, // 使用 el-upload 的 uid
    raw: uploadFile.raw // 保留原始文件对象
  };
  internalFileList.value = [uploadingFileEntry];
  // !! 此时需要 emit，因为用户操作改变了列表状态 !!
  emit('update:fileList', internalFileList.value);

  let qiniuToken = '';
  try {
    // 1. 获取七牛云 Token
    qiniuToken = await getQiniuToken();
  } catch (error) {
    ElMessage.error(error.message || '获取上传凭证失败');
    internalFileList.value = []; // 清空列表
    emit('update:modelValue', '');
    emit('update:fileList', []);
    emit('upload-error', error);
    isUploading.value = false;
    emit('file-dialog-closed');
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl); localPreviewUrl = null; // 清理预览
    return;
  }

  try {
    // 2. 调用上传服务
    const finalFileKey = await uploadToQiniu(uploadFile.raw, qiniuToken);

    // 3. 上传成功
    const finalFileUrl = `http://image.langtaichem.com/${finalFileKey}`; // 替换为你的图片域名
    const successFileEntry = {
      name: finalFileKey,
      url: finalFileUrl,
      status: 'success',
      uid: uploadFile.uid, // 保持 uid
    };
    internalFileList.value = [successFileEntry];

    // !! 成功后 emit 更新 !!
    emit('update:modelValue', finalFileKey);
    emit('update:fileList', internalFileList.value);
    emit('upload-success', finalFileKey);
    ElMessage.success('图片上传成功');

  } catch (error) {
    // 4. 上传失败
    console.error('ImageUpload: 上传失败', error);
    ElMessage.error(error.message || '图片上传失败，请重试');
    internalFileList.value = []; // 清空列表
    // !! 失败后 emit 更新 !!
    emit('update:modelValue', '');
    emit('update:fileList', []);
    emit('upload-error', error);
  } finally {
    // 5. 清理工作
    isUploading.value = false; // 清除上传中状态
    // console.log('ImageUpload: Upload process finished.');
    if (localPreviewUrl && internalFileList.value[0]?.status !== 'uploading') {
      // 如果上传完成（无论成功失败），且当前显示的不是本地预览 URL，则释放
      URL.revokeObjectURL(localPreviewUrl);
      localPreviewUrl = null;
    }
    // 确保上传流程结束后关闭对话框状态
    emit('file-dialog-closed');
    // 清理 el-upload 内部列表，防止影响下次选择或显示旧文件
    uploader.value?.clearFiles();
  }
};

/** 处理图片预览 */
const handlePreview = () => {
  if (internalFileList.value.length && internalFileList.value[0].url) {
    dialogImageUrl.value = internalFileList.value[0].url;
    dialogVisible.value = true;
  }
};

/** 处理图片删除 */
const handleRemove = () => {
  if (isUploading.value) {
    ElMessage.warning('文件上传中，请稍后删除');
    return;
  }
  // 释放可能存在的本地预览 URL
  if (localPreviewUrl) {
    URL.revokeObjectURL(localPreviewUrl);
    localPreviewUrl = null;
  }
  // 清空内部列表
  internalFileList.value = [];
  isHovering.value = false; // 移除悬浮状态
  // !! 删除后 emit 更新 !!
  emit('update:modelValue', ''); // 清空 modelValue
  emit('update:fileList', []); // 清空 fileList
  // 可选：触发一个删除事件
  // emit('remove');
  // 不需要再 emit file-dialog-closed，因为删除不是文件对话框操作
};

// --- 生命周期和侦听器 ---

onMounted(() => {
  // console.log('ImageUpload: Mounted');
  updateInternalListFromProps(); // 组件挂载时根据 props 初始化内部状态
  // 不再需要 initQiniu()
});

onUnmounted(() => {
  // console.log('ImageUpload: Unmounted');
  // 组件卸载时确保释放本地预览 URL
  if (localPreviewUrl) {
    URL.revokeObjectURL(localPreviewUrl);
    localPreviewUrl = null;
  }
});

// 监听外部 modelValue 变化，更新内部状态
// !! 优化：增加检查，避免响应内部 emit 触发的更新 !!
watch(() => props.modelValue, (newVal, oldVal) => {
  // 只有当外部 modelValue 确实改变，并且与内部状态不一致时才更新
  const internalCurrentKey = internalFileList.value[0]?.status === 'success' ? internalFileList.value[0].name : null;
  if (newVal !== oldVal && newVal !== internalCurrentKey) {
    // console.log('ImageUpload: modelValue prop changed, updating internal list.');
    updateInternalListFromProps();
  }
});

// 监听外部 fileList 变化，更新内部状态 (仅当 modelValue 不存在时作为主要数据源)
// !! 优化：增加检查，避免响应内部 emit 触发的更新 !!
watch(() => props.fileList, (newList, oldList) => {
  if (!props.modelValue) { // 只有在没有 modelValue (不是已上传状态) 时，才响应 fileList prop
    const currentInternalJSON = JSON.stringify(internalFileList.value);
    const newPropsListJSON = JSON.stringify(newList || []);
    if (newPropsListJSON !== currentInternalJSON) {
      // console.log('ImageUpload: fileList prop changed (and no modelValue), updating internal list.');
      updateInternalListFromProps();
    }
  }
}, { deep: true }); // 仍然需要深度监听，以防外部修改了数组内部对象

</script>

<style scoped>
/* 样式保持不变 */
.image-upload-container {
  position: relative;
  width: 100px;
  height: 100px;
}

.hidden-uploader {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  z-index: -1;
}

.custom-upload-area {
  width: 100%;
  height: 100%;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
  background-color: #fbfdff;
}

.custom-upload-area:hover:not(.has-image) {
  border-color: #409eff;
}

.custom-upload-area.has-image {
  border: 1px solid #c0ccda;
  background: transparent;
}

.upload-icon {
  font-size: 24px;
  color: #8c939d;
}

.upload-text {
  margin-top: 6px;
  font-size: 12px;
  color: #8c939d;
}

.preview-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.action-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  border-radius: 6px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 1;
}

.preview-wrapper:hover .action-overlay {
  opacity: 1;
}

.action-icon {
  color: white;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s;
}

.action-icon:first-of-type:hover {
  color: #409eff;
}

.action-icon:last-of-type:hover {
  color: #ff4d4f;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.el-dialog__body) img {
  display: block;
  max-width: 100%;
  height: auto;
}
</style>